import { useCallback, useEffect, useState } from "react";
import { Checkbox } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { ListLayout } from "ui-components/Layout";
import { EditButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { CancelButton } from "ui-components/IconButton";
import { FormTitle } from "ui-components/Form";

import { useCustomerValue } from "providers/CustomerContext";
import { getDOStatusCount } from "services/data.outbound.service";

import { getDOList } from "services/data.outbound.service";
import { createDO } from "services/data.outbound.service";
import { cancelDO } from "services/data.outbound.service";

import DOLog from "./components/DOLog";
import CheckboxTable2 from "ui-components/tables/CheckboxTable2";
import { TableCard } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { LoadingDialog } from "ui-components/Alert";
import { PrintButton } from "ui-components/IconButton";
import { getDeliveryOrderReport } from "services/data.report.service";
import { useTeamValue } from "providers/TeamContext";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";

const statusObj = { Open: "O", Delivered: "D", Cancelled: "C" };

function DOList() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const enabled = Boolean(customerCode);
  const navigate = useNavigate();
  const location = useLocation();
  const { status2, message } = location?.state ?? {};
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState(status2 === "D" ? "Delivered" : "Open");
  const [tabs, setTabs] = useState([
    { label: "Open", count: 0 },
    { label: "Delivered", count: 0 },
    { label: "Cancelled", count: 0 },
  ]);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const addPermission =
    allowAll ||
    (Boolean(permissions["Delivery Order"]) &&
      permissions["Delivery Order"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Delivery Order"]) &&
      permissions["Delivery Order"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Delivery Order"]) &&
      permissions["Delivery Order"].findIndex((b) => b === "deactivate") !== -1);
  useEffect(() => {
    if (message) {
      setAlert(<Success content={message} onChange={setAlert} />);
    }
  }, [location.state]);
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const downloadReportMutation = useMutation(getDeliveryOrderReport, {
    onSuccess: (response) => {
      const fileType = response.headers["content-type"];
      const blob = new Blob([response.data], {
        type: fileType,
      });
      const url = window.URL.createObjectURL(blob);
      if (fileType.includes("pdf")) {
        const w = window.open(url);
        w.print();
        //a.href = null;
        window.URL.revokeObjectURL(url);
      }
    },
    onError: () => setAlert(<Error_ content="The report file not found" onChange={setAlert} />),
  });
  const [selectedIds, setSelectedIds] = useState([]);

  useQuery({
    queryKey: ["do-list", { customerCode, status, startDate }],
    queryFn: () => getDOList({ customerCode, status: statusObj[status], orderDate: startDate }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["do-list", "status", { customerCode }],
    queryFn: () => getDOStatusCount({ customerCode }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(cancelDO, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["do-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const createDOMutation = useMutation(createDO, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["do-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const onEdit = (req) => navigate("/outbound/do/edit", { state: req });

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Delete DO Number ${req.doNumber}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };

  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          sx={{
            padding: 0,
            "& .MuiSvgIcon-root": {
              borderColor: "#97b8d8",
            },
          }}
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          sx={{
            padding: 0,
            "& .MuiSvgIcon-root": {
              borderColor: "#97b8d8",
            },
          }}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          indeterminate={row.getIsSomeSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    { header: "Pickslip Number", accessorKey: "pickSlipNo" },
    { header: "Cust Order Number", accessorKey: "custOrdNbr" },
    { header: "Order Number", accessorKey: "orderNumber" },
    { header: "Order Date", accessorKey: "orderDate" },
    { header: "Consignee Code", accessorKey: "consigneeCode" },
    { header: "Order Type", accessorKey: "orderType" },
    { header: "Customer Code", accessorKey: "customerCode" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { pickSlipId, consigneeCode, orderType } = row.original;
        return status === "Open" ? (
          <>
            <MDButton
              variant="gradient"
              color="info"
              size="small"
              disabled={!addPermission}
              onClick={() => {
                const req = {
                  PickSlipList: [{ pickSlipId, consigneeCode, orderType }],
                };
                console.log(req);
                createDOMutation.mutate(req);
              }}
            >
              Generate DO
            </MDButton>
          </>
        ) : (
          <></>
        );
      },
    },
  ];

  const columns2 = [
    {
      header: "Cust Order Number",
      accessorKey: "custOrdNbr",
      width: "300px",
      cell: ({ row }) => {
        return <div>{row.original.custOrdNbr}</div>;
      },
    },
    {
      header: "DO Number",
      accessorKey: "doNumber",
      cell: ({ row }) => {
        const { delOrderId, doNumber } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ delOrderId });
            }}
          >
            {doNumber}
          </span>
        );
      },
    },
    { header: "DO Date", accessorKey: "doDate" },
    { header: "Warehouse Code", accessorKey: "warehouseCode" },
    { header: "Customer Code", accessorKey: "customerCode" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { delOrderId, doNumber } = row.original;
        return status === "Cancelled" ? (
          <></>
        ) : (
          <>
            {/* <EditButton
              title="Edit DO"
              onClick={() => {
                onEdit({ delOrderId });
              }}
            /> */}
            <CancelButton
              title="Cancel DO"
              disabeld={!deactivatePermission}
              onClick={() => {
                onDelete({ delOrderId, doNumber });
              }}
            />
            <PrintButton
              title="Print DO and Packing List"
              onClick={() => {
                downloadReportMutation.mutate({
                  customerCode,
                  doNumber: [doNumber],
                  reportType: "PDF",
                  printPL: true
                });
              }}
            />
            <MDButton variant="gradient" color="info" size="small" onClick={() => {
                downloadReportMutation.mutate({
                  customerCode,
                  doNumber: [doNumber],
                  reportType: "PDF",
                });
              }}>Print DO only</MDButton>
          </>
        );
      },
    },
  ];

  const combineDO = () => {
    const req = { pickSlipList: selectedIds };
    createDOMutation.mutate(req);
  };
  const createStatus = createDOMutation.status === "loading";
  const downloadStatus = downloadReportMutation.status === "loading";
  const handleChange = useCallback((data) => {
    setSelectedIds(data);
  }, []);

  return (
    <ListLayout>
      <DOLog {...logProp} />

      {(createStatus || downloadStatus) && <LoadingDialog />}
      {dialog}
      <MDBox my={3}>
        {alert}
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <FormTitle title="Delivery Order" />
        </MDBox>
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      {/* <Card sx={{ padding: "0 10px" }}>
                <BasicTable_ columns={status === "Open" ? columns : columns2} data={rows} clickDO={clickDO} module={status === "Open" ? "DO" : ""} />
            </Card> */}
      <TableCard>
        {status === "Open" ? (
          <>
            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              margin="10px"
            >
              <MDButton
                variant="gradient"
                color="info"
                onClick={combineDO}
                disabled={selectedIds.length === 0 || !addPermission}
              >
                COMBINE DO
              </MDButton>
            </MDBox>
            <CheckboxTable2 columns={columns} data={data} onSelectedRowsChange={handleChange} />
          </>
        ) : (
          <>
            <MDBox display="flex" justifyContent="flex-end">
              <MDBox margin="10px 10px 0 0">
                <CustomDateRangePicker {...customProps} />
              </MDBox>
            </MDBox>

            <BasicTable columns={columns2} data={data} />
          </>
        )}
      </TableCard>
    </ListLayout>
  );
}

export default DOList;
